import type {
  CdropdownAlign,
  CdropdownMode,
  CdropdownOpenDirection,
  DropdownPosition,
} from "./Cdropdown.types";
import {
  CARET_DROPDOWN_HEIGHT_PADDING_PX,
  CDROPDOWN_ALIGN,
  CDROPDOWN_MODE,
  CDROPDOWN_OPEN_DIRECTION,
  DROPDOWN_EDGE_MARGIN_PX,
  ESTIMATED_DROPDOWN_ITEM_HEIGHT_PX,
  FALLBACK_WIDTH_PX,
} from "./constants";

interface ResolveWidthPxInput {
  width: number | string;
  fallbackPx: number;
}

interface ResolveDropdownPositionInput {
  mode: CdropdownMode;
  anchorRect: DOMRect | null | undefined;
  triggerElement: HTMLElement | null;
  horizontalAnchorElement: HTMLElement | null | undefined;
  align: CdropdownAlign;
  width: number | string;
  matchAnchorWidth: boolean;
  maxHeight: number | undefined;
  measuredHeight: number | null;
  openDirection: CdropdownOpenDirection;
  optionsLength: number;
}

interface ResolvedDropdownPosition {
  position: DropdownPosition;
  anchorMeasuredWidth: number | null;
}

export const INITIAL_POSITION: DropdownPosition = {
  top: 0,
  bottom: null,
  left: 0,
  openUpwards: false,
};

function clamp(input: { value: number; min: number; max: number }): number {
  return Math.max(input.min, Math.min(input.value, input.max));
}

export function resolveWidthPx(input: ResolveWidthPxInput): number {
  if (typeof input.width === "number") {
    return input.width;
  }

  const remMatch = input.width.match(/^([0-9]*\.?[0-9]+)rem$/);
  if (remMatch) {
    const remValue = Number(remMatch[1]);
    if (!Number.isNaN(remValue)) {
      return remValue * 16;
    }
  }

  const pxMatch = input.width.match(/^([0-9]*\.?[0-9]+)px$/);
  if (pxMatch) {
    const pxValue = Number(pxMatch[1]);
    if (!Number.isNaN(pxValue)) {
      return pxValue;
    }
  }

  return input.fallbackPx;
}

function resolveEstimatedDropdownHeight(input: {
  maxHeight: number | undefined;
  measuredHeight: number | null;
  optionsLength: number;
}): number {
  const estimatedHeight =
    Math.min(
      input.maxHeight ?? Number.MAX_SAFE_INTEGER,
      input.optionsLength * ESTIMATED_DROPDOWN_ITEM_HEIGHT_PX,
    ) + CARET_DROPDOWN_HEIGHT_PADDING_PX;

  return input.measuredHeight && input.measuredHeight > 0
    ? input.measuredHeight
    : estimatedHeight;
}

function resolveDesiredLeft(input: {
  align: CdropdownAlign;
  anchorRect: DOMRect;
  widthPx: number;
}): number {
  if (input.align === CDROPDOWN_ALIGN.Center) {
    return input.anchorRect.left + (input.anchorRect.width - input.widthPx) / 2;
  }

  if (input.align === CDROPDOWN_ALIGN.Right) {
    return input.anchorRect.right - input.widthPx;
  }

  return input.anchorRect.left;
}

function resolveOpenUpwards(input: {
  anchorRect: DOMRect;
  effectiveHeight: number;
  openDirection: CdropdownOpenDirection;
  viewportHeight: number;
}): boolean {
  const spaceBelow =
    input.viewportHeight - input.anchorRect.bottom - DROPDOWN_EDGE_MARGIN_PX;
  const spaceAbove = input.anchorRect.top - DROPDOWN_EDGE_MARGIN_PX;

  return (
    input.openDirection === CDROPDOWN_OPEN_DIRECTION.Up ||
    (input.openDirection === CDROPDOWN_OPEN_DIRECTION.Auto &&
      spaceBelow < input.effectiveHeight &&
      spaceAbove > spaceBelow)
  );
}

function resolvePositionFromRect(input: {
  anchorRect: DOMRect;
  horizontalRect: DOMRect;
  align: CdropdownAlign;
  widthPx: number;
  effectiveHeight: number;
  openDirection: CdropdownOpenDirection;
  viewportWidth: number;
  viewportHeight: number;
  topWhenOpeningDown: number;
  bottomWhenOpeningUp: number;
  clampDownwardTop?: boolean;
}): DropdownPosition {
  const desiredLeft = resolveDesiredLeft({
    align: input.align,
    anchorRect: input.horizontalRect,
    widthPx: input.widthPx,
  });
  const clampedLeft = clamp({
    value: desiredLeft,
    min: DROPDOWN_EDGE_MARGIN_PX,
    max: input.viewportWidth - input.widthPx - DROPDOWN_EDGE_MARGIN_PX,
  });
  const shouldOpenUpwards = resolveOpenUpwards({
    anchorRect: input.anchorRect,
    effectiveHeight: input.effectiveHeight,
    openDirection: input.openDirection,
    viewportHeight: input.viewportHeight,
  });
  const downwardTop = input.clampDownwardTop
    ? Math.min(
        input.viewportHeight - DROPDOWN_EDGE_MARGIN_PX,
        input.topWhenOpeningDown,
      )
    : input.topWhenOpeningDown;

  return {
    top: shouldOpenUpwards ? 0 : downwardTop,
    bottom: shouldOpenUpwards ? input.bottomWhenOpeningUp : null,
    left: clampedLeft,
    openUpwards: shouldOpenUpwards,
  };
}

function resolveCaretDropdownPosition(input: {
  anchorRect: DOMRect | null | undefined;
  align: CdropdownAlign;
  width: number | string;
  effectiveHeight: number;
  openDirection: CdropdownOpenDirection;
  viewportWidth: number;
  viewportHeight: number;
}): ResolvedDropdownPosition | null {
  if (!input.anchorRect) {
    return null;
  }

  const widthPx = resolveWidthPx({
    width: input.width,
    fallbackPx: FALLBACK_WIDTH_PX,
  });

  return {
    position: resolvePositionFromRect({
      anchorRect: input.anchorRect,
      horizontalRect: input.anchorRect,
      align: input.align,
      widthPx,
      effectiveHeight: input.effectiveHeight,
      openDirection: input.openDirection,
      viewportWidth: input.viewportWidth,
      viewportHeight: input.viewportHeight,
      topWhenOpeningDown: input.anchorRect.bottom + DROPDOWN_EDGE_MARGIN_PX,
      bottomWhenOpeningUp:
        input.viewportHeight - input.anchorRect.top + DROPDOWN_EDGE_MARGIN_PX,
      clampDownwardTop: true,
    }),
    anchorMeasuredWidth: null,
  };
}

function resolveTriggerWidth(input: {
  horizontalAnchorElement: HTMLElement | null | undefined;
  horizontalRect: DOMRect;
  matchAnchorWidth: boolean;
  width: number | string;
}): number {
  if (input.matchAnchorWidth && input.horizontalAnchorElement) {
    return input.horizontalRect.width;
  }

  return resolveWidthPx({ width: input.width, fallbackPx: FALLBACK_WIDTH_PX });
}

function resolveTriggerAnchorMeasuredWidth(input: {
  horizontalAnchorElement: HTMLElement | null | undefined;
  horizontalRect: DOMRect;
  matchAnchorWidth: boolean;
}): number | null {
  return input.matchAnchorWidth && input.horizontalAnchorElement
    ? input.horizontalRect.width
    : null;
}

function resolveTriggerDropdownPosition(input: {
  triggerElement: HTMLElement | null;
  horizontalAnchorElement: HTMLElement | null | undefined;
  align: CdropdownAlign;
  width: number | string;
  matchAnchorWidth: boolean;
  effectiveHeight: number;
  openDirection: CdropdownOpenDirection;
  viewportWidth: number;
  viewportHeight: number;
}): ResolvedDropdownPosition | null {
  if (!input.triggerElement) {
    return null;
  }

  const triggerRect = input.triggerElement.getBoundingClientRect();
  const horizontalRect = input.horizontalAnchorElement
    ? input.horizontalAnchorElement.getBoundingClientRect()
    : triggerRect;
  const widthPx = resolveTriggerWidth({
    horizontalAnchorElement: input.horizontalAnchorElement,
    horizontalRect,
    matchAnchorWidth: input.matchAnchorWidth,
    width: input.width,
  });

  return {
    position: resolvePositionFromRect({
      anchorRect: triggerRect,
      horizontalRect,
      align: input.align,
      widthPx,
      effectiveHeight: input.effectiveHeight,
      openDirection: input.openDirection,
      viewportWidth: input.viewportWidth,
      viewportHeight: input.viewportHeight,
      topWhenOpeningDown: triggerRect.bottom + DROPDOWN_EDGE_MARGIN_PX,
      bottomWhenOpeningUp:
        input.viewportHeight - triggerRect.top + DROPDOWN_EDGE_MARGIN_PX,
    }),
    anchorMeasuredWidth: resolveTriggerAnchorMeasuredWidth({
      horizontalAnchorElement: input.horizontalAnchorElement,
      horizontalRect,
      matchAnchorWidth: input.matchAnchorWidth,
    }),
  };
}

export function resolveDropdownPosition(
  input: ResolveDropdownPositionInput,
): ResolvedDropdownPosition | null {
  const viewportWidth = document.documentElement.clientWidth;
  const viewportHeight = document.documentElement.clientHeight;
  const effectiveHeight = resolveEstimatedDropdownHeight({
    maxHeight: input.maxHeight,
    measuredHeight: input.measuredHeight,
    optionsLength: input.optionsLength,
  });

  if (input.mode === CDROPDOWN_MODE.Caret) {
    return resolveCaretDropdownPosition({
      anchorRect: input.anchorRect,
      align: input.align,
      width: input.width,
      effectiveHeight,
      openDirection: input.openDirection,
      viewportWidth,
      viewportHeight,
    });
  }

  return resolveTriggerDropdownPosition({
    triggerElement: input.triggerElement,
    horizontalAnchorElement: input.horizontalAnchorElement,
    align: input.align,
    width: input.width,
    matchAnchorWidth: input.matchAnchorWidth,
    effectiveHeight,
    openDirection: input.openDirection,
    viewportWidth,
    viewportHeight,
  });
}
